import { AchievementSchema, ChallengeSchema, LoyaltyConfigSchema, TierSchema } from '@kilat/shared';
import { createRepo } from './_base.js';

/**
 * Operator-side loyalty-program repos. These mirror the admin-SDK repos in
 * `apps/functions/src/lib/repositories/loyalty-repos.ts` so the dashboard
 * editor writes EXACTLY what the points/tier engine (`onTicketCollected`)
 * reads — same doc paths, same schemas.
 *
 *   loyaltyConfigRepoFor(tid)  → tenants/{tid}/loyaltyProgram/config   (single doc, id = 'config')
 *   loyaltyTiersRepoFor(tid)   → tenants/{tid}/loyaltyProgram/config/tiers/{tierId}
 *
 * Writes are rules-guarded (owner/manager) — same pattern as catalog,
 * promotions, vouchers and branding. There is no cross-tenant economic
 * exposure: an operator only configures their OWN customers' earn rate/tiers,
 * so a direct client write behind the `hasTenantRole(tid, ['owner','manager'])`
 * rule is the correct, consistent guard (no callable needed).
 */

/** Firestore doc id of the single loyalty-config document. */
export const LOYALTY_CONFIG_DOC_ID = 'config';

export function loyaltyConfigRepoFor(tenantId: string) {
  return createRepo({
    path: `tenants/${tenantId}/loyaltyProgram`,
    schema: LoyaltyConfigSchema,
    name: `tenants/${tenantId}/loyaltyProgram`,
  });
}

export function loyaltyTiersRepoFor(tenantId: string) {
  return createRepo({
    path: `tenants/${tenantId}/loyaltyProgram/config/tiers`,
    schema: TierSchema,
    name: `tenants/${tenantId}/loyaltyProgram/config/tiers`,
  });
}

/**
 * Achievement definitions (`tenants/{tid}/loyaltyProgram/config/achievements`).
 * Operator-authored via /loyalty/achievements — full CRUD (create/edit/delete/
 * pause), rules-guarded (owner/manager) exactly like tiers/challenges/vouchers.
 * Seeded by tenantBootstrap (`defaultAchievements`) so a fresh tenant starts with
 * a ready-to-earn set; the operator then customises them. Each def is built +
 * validated by the pure `buildAchievementDefinition` (reward forced to `none`).
 * `onTicketCollected` reads the active ones server-side to evaluate badges.
 */
export function achievementsRepoFor(tenantId: string) {
  return createRepo({
    path: `tenants/${tenantId}/loyaltyProgram/config/achievements`,
    schema: AchievementSchema,
    name: `tenants/${tenantId}/loyaltyProgram/config/achievements`,
  });
}

/**
 * Challenge definitions (`tenants/{tid}/loyaltyProgram/config/challenges`).
 * Operator-authored via /loyalty/challenges — full CRUD (create/edit/delete),
 * rules-guarded (owner/manager) exactly like tiers/vouchers. No seed: the
 * subcollection is empty until the operator makes a challenge. `onTicketCollected`
 * reads the active ones server-side to advance windowed progress + issue rewards.
 */
export function challengesRepoFor(tenantId: string) {
  return createRepo({
    path: `tenants/${tenantId}/loyaltyProgram/config/challenges`,
    schema: ChallengeSchema,
    name: `tenants/${tenantId}/loyaltyProgram/config/challenges`,
  });
}
